import type { Control, FieldValues } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import React from 'react';
import classNames from 'classnames';
import Icon from '@/components/Icon/Icon';
import useViewport from '@/common/context/ViewPort/useViewPort';
import './PromoCodeInput.scss';
import Input from '@/components/Input/Input';
import type { PromoCodeData } from '@/common/interfaces/promoCode.interface';
import { onlySpaceRegex } from '@/components/utils/FunctionUtils';
import { isAlphanumeric } from '@/utilities/CommonUtils';
import { PROMO_CODE_TYPES } from '@/constants/Common';

interface IPromoCodeInput {
  promoFocus: boolean;
  promoCodeData: PromoCodeData | undefined;
  promoApplied: boolean;
  promoCodeError: string;
  setPromoCodeError: React.Dispatch<React.SetStateAction<string>>;
  onPromoApplyClick: () => void;
  onPromoRemoveClick: () => void;
  control?: Control<FieldValues, unknown>;
  isSubtotalErrorMessage: boolean;
}

const PromoCodeInput: React.FC<IPromoCodeInput> = ({
  promoFocus,
  promoCodeData,
  promoApplied,
  promoCodeError,
  setPromoCodeError,
  onPromoApplyClick,
  onPromoRemoveClick,
  isSubtotalErrorMessage,
  control: propControl,
}) => {
  const screen = useViewport(true);
  const styleClass = `promo-code-input--${screen}`;
  const { control: formControl } = useFormContext();
  const control = propControl || formControl;
  const promoCodeErrorClass = isSubtotalErrorMessage ? 'subtotal-error-input-msg' : '';
  /* eslint-disable no-underscore-dangle */
  /** Vars for promo code logic */
  const applyClassNames = classNames({
    [`${styleClass}__apply-inactive`]: !control._formValues.promoCode,
    [`${styleClass}__apply-active`]: control._formValues.promoCode,
  });
  const discIconClassNames = classNames({
    [`${styleClass}__discount-icon-inactive`]: !control._formValues.promoCode,
    [`${styleClass}__discount-icon-active`]: control._formValues.promoCode,
  });

  /** This renders the discount icon inside promo code input */
  const discountIcon = (
    <span className={discIconClassNames} style={{ width: 20, height: 20, display: 'inline-flex' }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={20}
        height={20}
        viewBox="0 0 20 20"
        fill="none"
        style={{ width: 20, height: 20 }}
      >
        <path
          d="M8.75952 3.05489C8.91585 2.88031 9.10723 2.74065 9.32118 2.64503C9.53514 2.54942 9.76685 2.5 10.0012 2.5C10.2355 2.5 10.4672 2.54942 10.6812 2.64503C10.8952 2.74065 11.0865 2.88031 11.2429 3.05489L11.8262 3.70656C11.9929 3.89281 12.1994 4.03917 12.4303 4.13481C12.6613 4.23044 12.9108 4.27291 13.1604 4.25906L14.0354 4.21073C14.2694 4.19782 14.5035 4.23443 14.7224 4.31816C14.9414 4.40189 15.1402 4.53085 15.3059 4.69663C15.4716 4.86241 15.6005 5.06128 15.6841 5.28025C15.7677 5.49922 15.8042 5.73336 15.7912 5.96739L15.7429 6.84156C15.7291 7.091 15.7717 7.34034 15.8673 7.57113C15.9629 7.80192 16.1092 8.00826 16.2954 8.17489L16.947 8.75823C17.1217 8.91455 17.2615 9.10599 17.3572 9.32002C17.4529 9.53405 17.5024 9.76586 17.5024 10.0003C17.5024 10.2348 17.4529 10.4666 17.3572 10.6806C17.2615 10.8946 17.1217 11.0861 16.947 11.2424L16.2954 11.8257C16.1091 11.9924 15.9628 12.1989 15.8671 12.4299C15.7715 12.6608 15.729 12.9103 15.7429 13.1599L15.7912 14.0349C15.8041 14.2689 15.7675 14.5031 15.6838 14.722C15.6 14.9409 15.4711 15.1397 15.3053 15.3054C15.1395 15.4711 14.9406 15.6 14.7217 15.6836C14.5027 15.7672 14.2686 15.8037 14.0345 15.7907L13.1604 15.7424C12.9109 15.7287 12.6616 15.7712 12.4308 15.8668C12.2 15.9625 11.9937 16.1088 11.827 16.2949L11.2437 16.9466C11.0874 17.1213 10.8959 17.2611 10.6819 17.3568C10.4679 17.4525 10.2361 17.5019 10.0016 17.5019C9.76716 17.5019 9.53535 17.4525 9.32132 17.3568C9.10728 17.2611 8.91585 17.1213 8.75952 16.9466L8.17619 16.2949C8.00947 16.1086 7.803 15.9623 7.57205 15.8666C7.34111 15.771 7.09161 15.7285 6.84202 15.7424L5.96702 15.7907C5.73299 15.8036 5.49886 15.767 5.27993 15.6833C5.061 15.5996 4.8622 15.4706 4.6965 15.3048C4.5308 15.139 4.40192 14.9402 4.3183 14.7212C4.23468 14.5022 4.19818 14.2681 4.21119 14.0341L4.25952 13.1599C4.27325 12.9104 4.23072 12.6611 4.13509 12.4303C4.03945 12.1995 3.89316 11.9932 3.70702 11.8266L3.05536 11.2432C2.88063 11.0869 2.74086 10.8955 2.64516 10.6814C2.54946 10.4674 2.5 10.2356 2.5 10.0011C2.5 9.76669 2.54946 9.53488 2.64516 9.32085C2.74086 9.10682 2.88063 8.91539 3.05536 8.75906L3.70702 8.17573C3.89327 8.00901 4.03963 7.80253 4.13527 7.57159C4.23091 7.34064 4.27338 7.09114 4.25952 6.84156L4.21119 5.96656C4.19841 5.73259 4.23511 5.49855 4.31889 5.27973C4.40268 5.0609 4.53166 4.8622 4.69743 4.69659C4.86319 4.53098 5.06202 4.40218 5.28092 4.31861C5.49983 4.23503 5.7339 4.19856 5.96786 4.21156L6.84202 4.25989C7.09147 4.27362 7.34081 4.23109 7.5716 4.13545C7.80239 4.03982 8.00873 3.89353 8.17536 3.70739L8.75952 3.05489Z"
          stroke="#8D90AA"
          strokeWidth="1.25"
        />
        <path
          d="M7.91699 7.91699H7.92533V7.92533H7.91699V7.91699ZM12.0837 12.0837H12.092V12.092H12.0837V12.0837Z"
          stroke="#8D90AA"
          strokeWidth="1.875"
          strokeLinejoin="round"
        />
        <path
          d="M12.5 7.5L7.5 12.5"
          stroke="#8D90AA"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );

  /** This renders the promo code type inside input on right side beside value */
  const percentSign = promoCodeData?.type === PROMO_CODE_TYPES.percentage ? '%' : null;
  const dollarSign = ![PROMO_CODE_TYPES.percentage, PROMO_CODE_TYPES.noGuaranteeFee].includes(
    promoCodeData?.type ?? -1
  )
    ? '$'
    : null;
  const promoValue =
    promoCodeData?.type !== PROMO_CODE_TYPES.noGuaranteeFee ? promoCodeData?.value : null;

  /** This renders the right side content of promo code input */
  const rightContent =
    control._formValues.promoCode && promoApplied ? (
      <>
        {promoCodeData?.discount ? (
          <span className={`${styleClass}__discount-value`}>
            {dollarSign}
            {promoValue}
            {percentSign}
          </span>
        ) : null}
        <Icon
          height="11px"
          width="11px"
          source="/src/assets/svg/close.svg"
          className={`${styleClass}__x-icon`}
        />
      </>
    ) : (
      <div className={applyClassNames} aria-label="Apply">
        Apply
      </div>
    );

  return (
    <div className={styleClass}>
      <Controller
        render={({ field }) => (
          <Input
            {...field}
            className={`${styleClass}__input`}
            label=""
            placeholder="Have a promo code?"
            name="promoCode"
            contentBefore={discountIcon}
            contentAfter={rightContent}
            autoFocus={promoFocus}
            disabled={promoApplied}
            onChange={(e) => {
              if (promoApplied) {
                e.preventDefault();
                return;
              }
              if (e.target.value && !isAlphanumeric(e.target.value)) return;
              if (e.target.value.length && onlySpaceRegex.test(e.target.value)) return;
              field.onChange(e.target.value.toUpperCase());
              if (promoCodeError) {
                setPromoCodeError('');
              }
            }}
            fieldErrMessg={promoCodeError}
            onBlur={(e) => {
              field.onChange(e.target.value.trim().toUpperCase());
              if (!e.target.value.trim().length) {
                setPromoCodeError('');
              }
            }}
            onContentAfterClick={
              control._formValues.promoCode && promoApplied ? onPromoRemoveClick : onPromoApplyClick
            }
            fieldErrMessgClass={promoCodeErrorClass}
          />
        )}
        control={control}
        rules={{
          validate: () => promoCodeError,
        }}
        name="promoCode"
      />
    </div>
  );
};

PromoCodeInput.displayName = 'PromoCodeInput';

export default PromoCodeInput;
